import { useTransition } from "react";
import { useSetAtom } from "jotai";
import {
	faCircleCheck,
	faCircleInfo,
	faCircleXmark,
	faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";

import STYLES from "@vita-learning/styles";

import { popupAtom } from "./popupAtom";

import type { PopupProps } from "./Popup.d";

export const usePopupQueue = () => {
	const setQueue = useSetAtom(popupAtom);
	const [, startTransition] = useTransition();

	/**
	 * Add alert props to the end of the alert queue
	 * @function enqueue
	 * @param {AlertProps} props alert props
	 * @returns {void} side-effect of new prop at the end of the queue
	 */
	const enqueue = (props: PopupProps) =>
		startTransition(() => setQueue(q => [...q, props]));

	/**
	 *
	 * @param {string} message
	 * @param {string | undefined} title
	 * @param {IconDefinition | undefined} icon
	 * @returns {void}
	 */
	const enqueueSuccess = (
		message: string,
		title?: string,
		icon = faCircleCheck
	) =>
		enqueue({
			color: STYLES.colors.lights.backgrounds.successes.normal,
			title,
			message,
			icon,
		});

	const enqueueInfo = (message: string, title?: string, icon = faCircleInfo) =>
		enqueue({
			color: STYLES.colors.lights.backgrounds.infos.normal,
			title,
			message,
			icon,
		});

	/**
	 *
	 * @param message
	 * @param title
	 * @param {IconDefinition | undefined} icon
	 * @returns {void}
	 */
	const enqueueWarning = (
		message: string,
		title?: string,
		icon = faExclamationCircle
	) =>
		enqueue({
			color: STYLES.colors.lights.backgrounds.warnings.normal,
			title,
			message,
			icon,
		});

	/**
	 *
	 * @param {string} message
	 * @param {string | undefined} title
	 * @param {IconDefinition | undefined} icon
	 * @returns {void}
	 */
	const enqueueError = (
		message: string,
		title?: string,
		icon = faCircleXmark
	) =>
		enqueue({
			color: STYLES.colors.lights.backgrounds.dangers.normal,
			title,
			message,
			icon,
		});

	return {
		enqueue,
		enqueueError,
		enqueueSuccess,
		enqueueWarning,
		enqueueInfo,
	};
};
